import { useRating } from '@risksmart-app/components/src/hooks/useRating';
import {
  Assessment_Activity_Status_Enum,
  Assessment_Status_Enum,
  Issue_Assessment_Status_Enum,
} from '@risksmart-app/web-graphql-client/generated/graphql';
import { useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { v4 as uuidv4 } from 'uuid';

import type { FilterModal } from '@/components/customisable-ribbon/customisableRibbonModalSchema';
import { useCommonLookupLazy } from '@/hooks/useCommonLookupLazy';

import { useGetTypes } from './config';
import { useGetRibbonItemVisibilities } from './hooks/useGetRibbonItemVisibilities';

export const useGetDefaultRibbonFilters = (): FilterModal[] => {
  const { t } = useTranslation(['common'], {
    keyPrefix: 'dashboard.myItemsDashboard.ribbonTitles',
  });
  const types = useGetTypes();
  const visibilities = useGetRibbonItemVisibilities();

  const { getLabel: getActionLabel } = useRating('action_status');
  const { getLabel: getActivityLabel } = useRating(
    'assessment_activity_status'
  );
  const { getLabel: getAssessmentLabel } = useRating('assessment_status');
  const { getLabel: getIssueLabel } = useRating('issue_assessment_status');
  const { getByValue: getCommonLookupByValue } = useCommonLookupLazy();

  return useMemo(() => {
    // Each default tab is gated by the user's entity-type visibility so users
    // who lack access to (e.g.) the Policy module don't see a "My Documents"
    // tab that can never match anything (RSP-4427). The first tuple element
    // is the visibility flag; if it's false the tab is skipped entirely.
    const candidates: (false | FilterModal)[] = [
      visibilities.actions && {
        id: uuidv4(),
        title: t('actions'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.action,
            },
            // Matches the pre-refactor tile behaviour: show everything that
            // isn't Closed, including Open, Pending, and Overdue.
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getActionLabel('closed'),
            },
          ],
          operation: 'and',
        },
      },
      visibilities.risks && {
        id: uuidv4(),
        title: t('risks'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.risk,
            },
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              // Use the same lookup that useFlattenRibbonData uses for risk StatusLabelled
              value:
                getCommonLookupByValue('statuses', 'retired')?.label ??
                'Retired',
            },
          ],
          operation: 'and',
        },
      },
      visibilities.rcsa && {
        id: uuidv4(),
        title: t('rcsaActivities'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.RCSAActivity,
            },
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getActivityLabel(Assessment_Activity_Status_Enum.Complete),
            },
          ],
          operation: 'and',
        },
      },
      visibilities.indicators && {
        id: uuidv4(),
        title: t('indicators'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.indicator,
            },
          ],
          operation: 'and',
        },
      },
      visibilities.policies && {
        id: uuidv4(),
        title: t('documents'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.document,
            },
          ],
          operation: 'and',
        },
      },
      visibilities.assessments && {
        id: uuidv4(),
        title: t('assessments'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.assessment,
            },
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getAssessmentLabel(Assessment_Status_Enum.Complete),
            },
          ],
          operation: 'and',
        },
      },
      visibilities.controls && {
        id: uuidv4(),
        title: t('controls'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.control,
            },
          ],
          operation: 'and',
        },
      },
      visibilities.issues && {
        id: uuidv4(),
        title: t('issues'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.issue,
            },
            // Matches the pre-refactor tile behaviour: show everything that
            // isn't Closed (Pending / AwaitingClosure / FirstLineApproval /
            // Declined all remain visible).
            {
              operator: '!=',
              propertyKey: 'StatusLabelled',
              value: getIssueLabel(Issue_Assessment_Status_Enum.Closed),
            },
          ],
          operation: 'and',
        },
      },
      visibilities.obligations && {
        id: uuidv4(),
        title: t('obligations'),
        itemFilterQuery: {
          tokens: [],
          tokenGroups: [
            {
              operator: '=',
              propertyKey: 'Type',
              value: types.obligationReview,
            },
          ],
          operation: 'and',
        },
      },
    ];

    return candidates.filter((f): f is FilterModal => Boolean(f));
  }, [
    t,
    types,
    visibilities,
    getActionLabel,
    getActivityLabel,
    getAssessmentLabel,
    getIssueLabel,
    getCommonLookupByValue,
  ]);
};
